/**
 * useAuth Hook - Unified Frontend Auth/RBAC Adapter
 * 
 * This project previously had multiple overlapping auth implementations.
 * The Playwright suite and most UI state is driven by `stores/authStore` which
 * persists to `localStorage` under `raptor-auth-store`.
 * 
 * This hook is the single public interface for UI RBAC decisions.
 *
 * SECURITY NOTE:
 * Client-side RBAC is UX only. Backend must enforce authorization.
 */

import { useMemo } from 'react';

import { useAuthStore } from '../stores/authStore';
import type { AuthStatus, Permission, Role, User, UserRole } from '../types/auth';

export type { AuthStatus, Permission, Role, User, UserRole };
export { useAuthStore };

export interface UseAuthReturn {
  user: User | null;
  status: AuthStatus;
  isAuthenticated: boolean;
  isLoading: boolean;
  isSessionValid: boolean;

  hasPermission: (permission: Permission) => boolean;
  hasAllPermissions: (permissions: Permission[]) => boolean;
  hasAnyPermission: (permissions: Permission[]) => boolean;
  hasMinimumRole: (role: UserRole) => boolean;
}

export function useAuth(): UseAuthReturn {
  const user = useAuthStore((s) => s.user);
  const status = useAuthStore((s) => s.status);
  const hasHydrated = useAuthStore((s) => s.hasHydrated);
  const hasPermission = useAuthStore((s) => s.hasPermission);
  const hasAllPermissions = useAuthStore((s) => s.hasAllPermissions);
  const hasAnyPermission = useAuthStore((s) => s.hasAnyPermission);
  const hasMinimumRole = useAuthStore((s) => s.hasMinimumRole);

  // Avoid premature redirects during zustand-persist rehydration.
  const isLoading = !hasHydrated || status === 'loading';

  // Treat MFA-required sessions as authenticated (step-up required).
  const isAuthenticated = status === 'authenticated' || status === 'mfa_required';
  
  // Session validity is expressed via explicit auth status.
  const isSessionValid = useMemo(() => status !== 'session_expired', [status]);
  
  return {
    user,
    status,
    isAuthenticated,
    isLoading,
    isSessionValid,
    hasPermission,
    hasAllPermissions,
    hasAnyPermission,
    hasMinimumRole,
  };
}

/**
 * CSRF token access (if/when backend provides one).
 *
 * Current frontend auth store does not manage CSRF tokens; return empty string
 * to allow callers to include the header when a token is available.
 */
export function getCsrfToken(): string {
  return '';
}

/**
 * Default auth headers for API clients (best-effort).
 * - Always includes Content-Type.
 * - Includes Authorization Bearer token when present in the auth store.
 */
export function getAuthHeaders(): Record<string, string> {
  const token = useAuthStore.getState().token?.accessToken;
  if (token && typeof token === 'string' && token.length > 0) {
    return { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };
  }
  return { 'Content-Type': 'application/json' };
}

export default useAuth;

